import React, { useRef, useEffect, useMemo } from 'react';
import { useMany } from '@pankod/refine-core';
import { IResourceComponentsProps, useShow } from '@pankod/refine-core';
import {
  Show,
  RefreshButton,
  TextField,
  Row,
  Col,
  TagField,
  ListButton,
  Space,
  Card,
  DateField,
  MarkdownField,
  Typography,
  Timeline,
} from '@pankod/refine-antd';

import { LeadTypes, TimelineTypes } from 'interfaces';

import { colorDecider, textDecider } from 'utils';

const { Title } = Typography;

const LeadData: React.FC<IResourceComponentsProps> = () => {
  const ref: any = useRef(null);
  const { queryResult } = useShow<LeadTypes>({
    resource: 'leads',
    metaData: {
      fields: [
        'id',
        'source',
        'note',
        'name',
        'email',
        'phone',
        'country',
        'company',
        'message',
        'performed_by',
        'status',
        'createdAt',
      ],
    },
  });
  const { data, isLoading } = queryResult;
  const record: any = data?.data;

  const { data: timelimeData, isLoading: loading } = useMany<TimelineTypes>({
    resource: 'timelines',
    metaData: {
      fields: ['id', 'note', 'performed_by', 'action'],
    },
    ids: [record?.id],
    queryOptions: {
      enabled: record?.id ? true : false,
    },
  });

  const timelines: TimelineTypes[] = useMemo(
    () => timelimeData?.data ?? [],
    [timelimeData?.data]
  );

  useEffect(() => {
    ref?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [timelines]);

  return (
    <Show
      isLoading={isLoading}
      headerProps={{
        extra: (
          <Space>
            <ListButton />
            <RefreshButton onClick={() => queryResult.refetch()} />
          </Space>
        ),
      }}
    >
      <Row gutter={[30, 30]}>
        <Col lg={8} xs={24}>
          <Row gutter={[0, 10]}>
            <Col span={24}>
              <TextField strong value='ID' />
            </Col>
            <Col span={24}>
              <TagField value={record?.id} />
            </Col>
            <Col span={24}>
              <TextField strong value='Source' />
            </Col>
            <Col span={24}>
              <TagField value={record?.source} />
            </Col>
            <Col span={24}>
              <TextField strong value='Name' />
            </Col>
            <Col span={24}>
              <TagField value={record?.name} />
            </Col>
            <Col span={24}>
              <TextField strong value='Email' />
            </Col>
            <Col span={24}>
              <TagField value={record?.email} />
            </Col>
            <Col span={24}>
              <TextField strong value='Phone' />
            </Col>
            <Col span={24}>
              <TagField value={record?.phone} />
            </Col>
            <Col span={24}>
              <TextField strong value='Country' />
            </Col>
            <Col span={24}>
              <TagField value={record?.country ?? '----'} />
            </Col>
            <Col span={24}>
              <TextField strong value='Company' />
            </Col>
            <Col span={24}>
              <TagField value={record?.company ?? '----'} />
            </Col>
            <Col span={24}>
              <TextField strong value='Message' />
            </Col>
            <Col span={24}>
              <MarkdownField value={record?.message} />
            </Col>
            <Col span={24}>
              <TextField strong value='Note' />
            </Col>
            <Col span={24}>
              <MarkdownField value={record?.note} />
            </Col>
          </Row>
        </Col>
        <Col lg={16} xs={24}>
          <Card
            className='timeline-data'
            title='Timeline'
            style={{ height: '585px', overflowY: 'auto' }}
            bodyStyle={{ overflowY: 'auto' }}
            headStyle={{
              position: 'sticky',
              top: 0,
              backgroundColor: '#2a132e',
              color: 'white',
              zIndex: 1,
            }}
          >
            {loading ? (
              <Title>Loading....</Title>
            ) : (
              <React.Fragment>
                {timelines?.length > 0 ? (
                  <Timeline>
                    {timelines?.map((timeline: TimelineTypes) => {
                      return (
                        <Timeline.Item>
                          {textDecider(timeline?.action, timeline.performed_by)}{' '}
                          <TagField
                            value={timeline?.action}
                            color={colorDecider(timeline?.action)}
                          />
                          <DateField format='LLL' value={timeline?.createdAt} />
                          <MarkdownField value={`Note: ${timeline?.note}`} />
                        </Timeline.Item>
                      );
                    })}
                    <div ref={ref} />
                  </Timeline>
                ) : null}
              </React.Fragment>
            )}
          </Card>
        </Col>
      </Row>
    </Show>
  );
};

export default LeadData;
